import { useCallback, useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { Avatar, Box, Theme, ThemeProvider, Typography } from '@mui/material';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';

import { LayoutBaseDeDashboard } from '../../shared/layouts';
import { Environment } from '../../shared/environment';
import { UsuarioService } from '../../shared/services/api/cadastros/UsuariosService';
import { ICotacoesMercado, MercadoService } from '../../shared/services/api/mercado';
import { formatarValorCotacao, formatarVariacao } from './formatacao';
import { CABECALHO_ESCURO } from './coresCabecalho';
import { PrevisaoTempo } from './PrevisaoTempo';
import { PainelCotacoes } from './PainelCotacoes';
import { NoticiasMercado } from './NoticiasMercado';
import { AcessosRapidos } from './AcessosRapidos';

interface IJwtPayload {
  iduser: string;
  img: string;
}

// Mini "letreiro" no cabeçalho — os números que a maioria olha primeiro.
const CODIGOS_CABECALHO = ['USD', 'EUR', 'IBOV', 'SELIC', 'GASOLINA'];

const INTERVALO_COTACOES_MS = 10 * 60 * 1000;

// O tema do sistema fixa uma cor em cada variante de texto (ex.: caption =
// cinza "muted", pensado pro fundo das páginas). Dentro do cabeçalho, que
// tem fundo próprio (escuro ou claro), isso deixava textos sem cor explícita
// ilegíveis — aqui as variantes passam a herdar a cor do cabeçalho; quem
// precisa de outro tom (secundário, destaque) continua definindo no sx.
const VARIANTES_TEXTO = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'subtitle1', 'subtitle2', 'body1', 'body2', 'caption', 'overline'] as const;
const temaTextoHerdado = (temaExterno: Theme): Theme => {
  const typography = { ...temaExterno.typography };
  VARIANTES_TEXTO.forEach(variante => {
    typography[variante] = { ...temaExterno.typography[variante], color: 'inherit' };
  });
  return { ...temaExterno, typography };
};

const saudacao = () => {
  const hora = new Date().getHours();
  if (hora < 12) return 'Bom dia';
  if (hora < 18) return 'Boa tarde';
  return 'Boa noite';
};

const dataPorExtenso = () => {
  const texto = new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

// Painel inicial (rota /inicio) — primeira tela depois do login: boas-vindas
// com previsão do tempo, cotações de mercado, notícias e acessos rápidos.
// Mesma organização da tela Início do SifWeb, na identidade visual do SIAC.
export const PaginaInicial: React.FC = () => {
  const [nomeUsuario, setNomeUsuario] = useState('');
  const [fotoUsuario, setFotoUsuario] = useState('');

  // Cotações ficam aqui (e não dentro do PainelCotacoes) porque o letreiro
  // do cabeçalho usa os mesmos dados — uma busca só pros dois.
  const [cotacoes, setCotacoes] = useState<ICotacoesMercado | null>(null);
  const [carregandoCotacoes, setCarregandoCotacoes] = useState(true);
  const [erroCotacoes, setErroCotacoes] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('APP_ACCESS_TOKEN');
    if (!token) return;
    try {
      const decoded = jwtDecode<IJwtPayload>(token);
      if (decoded.img) setFotoUsuario(Environment.URL_IMGUSUARIOS + decoded.img);
      UsuarioService.getByIdPerfil(decoded.iduser).then(resultado => {
        if (!(resultado instanceof Error)) setNomeUsuario(resultado.nome);
      });
    } catch {
      // Token ilegível: o painel só fica sem nome/foto.
    }
  }, []);

  const carregarCotacoes = useCallback(() => {
    setCarregandoCotacoes(true);
    MercadoService.getCotacoes().then(resultado => {
      setCarregandoCotacoes(false);
      if (resultado instanceof Error) {
        setErroCotacoes(resultado.message);
        return;
      }
      setErroCotacoes(null);
      setCotacoes(resultado);
    });
  }, []);

  useEffect(() => {
    carregarCotacoes();
    const timer = setInterval(carregarCotacoes, INTERVALO_COTACOES_MS);
    return () => clearInterval(timer);
  }, [carregarCotacoes]);

  // Cabeçalho claro só no tema todo claro ("Claro (menu claro)").
  // Boas-vindas em degradê azul-petróleo com detalhes em ciano, nos dois temas.
  const cores = CABECALHO_ESCURO;

  const primeiroNome = nomeUsuario.trim().split(/\s+/)[0];
  const destaquesCabecalho = CODIGOS_CABECALHO
    .map(codigo => cotacoes?.itens.find(item => item.codigo === codigo))
    .filter((item): item is NonNullable<typeof item> => !!item);

  return (
    <LayoutBaseDeDashboard>
      <Box maxWidth={1400} mx="auto" py={{ xs: 1, sm: 1 }} display="flex" flexDirection="column" gap={{ xs: 2.5, md: 2 }}>

        {/* Cabeçalho — fundo escuro do painel de login nos temas com menu
            escuro; claro no tema todo claro (ver coresCabecalho.ts). */}
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 3,
            p: { xs: 2, md: 2.5 },
            color: cores.texto,
            background: cores.fundo,
            border: `1px solid ${cores.borda}`,
            // Grade de pontos bem apagada, a mesma "textura" do login.
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              backgroundImage: `radial-gradient(${cores.pontos} 1px, transparent 1px)`,
              backgroundSize: '22px 22px',
              maskImage: 'linear-gradient(90deg, transparent 0%, #000 60%)',
              pointerEvents: 'none',
            },
          }}
        >
          <ThemeProvider theme={temaTextoHerdado}>
          {/* Previsão desce pra baixo quando o cartão é estreito (container
              query: responde à largura do cartão, não da janela). */}
          <Box position="relative" sx={{ containerType: 'inline-size', containerName: 'boasvindas' }}>
            <Box
              display="grid"
              gap={2}
              alignItems="stretch"
              sx={{
                gridTemplateColumns: '1fr',
                '@container boasvindas (min-width: 900px)': { gridTemplateColumns: 'minmax(0, 1fr) minmax(420px, 560px)' },
              }}
            >
              <Box minWidth={0} display="flex" flexDirection="column" justifyContent="space-between" gap={2}>
                <Box display="flex" gap={1.5} alignItems="center">
                  <Avatar
                    src={fotoUsuario}
                    sx={{ width: 48, height: 48, flexShrink: 0, border: `2px solid ${cores.avatarBorda}` }}
                  />
                  <Box minWidth={0}>
                    <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 750, lineHeight: 1.2, letterSpacing: '-0.02em' }} noWrap>
                      {saudacao()}{primeiroNome ? `, ${primeiroNome}` : ''}!
                    </Typography>
                    <Typography variant="body2" sx={{ color: cores.textoSecundario }}>
                      {dataPorExtenso()} · Bem-vindo ao SIAC
                    </Typography>
                  </Box>
                </Box>

                {destaquesCabecalho.length > 0 && (
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {destaquesCabecalho.map(cotacao => {
                      const variacao = formatarVariacao(cotacao);
                      const subiu = (cotacao.variacao ?? 0) > 0;
                      const caiu = (cotacao.variacao ?? 0) < 0;
                      return (
                        <Box
                          key={cotacao.codigo}
                          display="flex"
                          alignItems="center"
                          gap={0.75}
                          px={1.25}
                          py={0.5}
                          borderRadius={999}
                          sx={{ bgcolor: cores.painelFundo, border: `1px solid ${cores.painelBorda}` }}
                        >
                          <Typography variant="caption" sx={{ color: cores.textoSecundario }}>{cotacao.nome}</Typography>
                          <Typography variant="caption" fontWeight={700}>{formatarValorCotacao(cotacao)}</Typography>
                          {variacao && (
                            <Box display="flex" alignItems="center" sx={{ color: subiu ? cores.subiu : caiu ? cores.caiu : cores.textoSecundario }}>
                              {subiu && <ArrowDropUpRoundedIcon sx={{ fontSize: 16, mx: -0.5 }} />}
                              {caiu && <ArrowDropDownRoundedIcon sx={{ fontSize: 16, mx: -0.5 }} />}
                              <Typography variant="caption" fontWeight={700} sx={{ fontSize: 11 }}>{variacao.replace(/^[+-]/, '')}</Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>

              <Box minWidth={0}>
                <PrevisaoTempo cores={cores} />
              </Box>
            </Box>
          </Box>
          </ThemeProvider>
        </Box>

        <PainelCotacoes
          dados={cotacoes}
          carregando={carregandoCotacoes}
          erro={erroCotacoes}
          onAtualizar={carregarCotacoes}
        />

        <NoticiasMercado />

        <AcessosRapidos />
      </Box>
    </LayoutBaseDeDashboard>
  );
};
